import React, { useState, useEffect } from 'react';
import './InstallPWA.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faTimes, faEllipsisV, faLaptop } from '@fortawesome/free-solid-svg-icons';
import baseURL from '../url';

// Icono de Compartir de Safari (Cuadrado con flecha hacia arriba)
const SafariShareIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle', margin: '0 4px', color: '#007AFF' }}>
        <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
        <polyline points="16 6 12 2 8 6" />
        <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
);

// Icono de agregar a pantalla de inicio en Apple (Cuadrado con un más en el centro)
const ApplePlusIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle', margin: '0 4px', color: '#8E8E93' }}>
        <rect x="3" y="3" width="18" height="18" rx="4" ry="4" />
        <line x1="12" y1="8" x2="12" y2="16" />
        <line x1="8" y1="12" x2="16" y2="12" />
    </svg>
);

export default function InstallPWA() {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showPopup, setShowPopup] = useState(false);
    const [pwaConfig, setPwaConfig] = useState(null);
    const [activeInstructions, setActiveInstructions] = useState(null); // 'ios' | 'android' | 'pc' | null
    const [isStandalone, setIsStandalone] = useState(false);

    useEffect(() => {
        // Detectar si la app ya está corriendo en modo standalone (instalada) o ya fue guardada como instalada
        const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone || localStorage.getItem('pwa_installed') === 'true';
        setIsStandalone(!!standalone);
        if (standalone) {
            document.documentElement.classList.add('pwa-standalone');
        }

        // Cargar configuración PWA
        fetch(`${baseURL}/pwaGet.php`)
            .then(response => response.json())
            .then(data => {
                setPwaConfig(data.pwa);
                // Solo mostrar popup si PWA está activo y no está instalada
                if (data.pwa && data.pwa.activo === 1 && !standalone) {
                    const popupMostrado = sessionStorage.getItem('pwaPopupMostrado');
                    if (!popupMostrado) {
                        setShowPopup(true);
                        sessionStorage.setItem('pwaPopupMostrado', 'true');
                    }
                }
            })
            .catch(error => console.error('Error al cargar configuración PWA:', error));

        // Capturar prompt de instalación nativo
        if (window.deferredPrompt) {
            setDeferredPrompt(window.deferredPrompt);
        }

        const handlePromptAvailable = () => {
            if (window.deferredPrompt) {
                setDeferredPrompt(window.deferredPrompt);
            }
        };

        const handleBeforeInstallPrompt = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };

        // Escuchar el evento global para forzar la visualización desde cualquier botón de descarga (ej: Footer)
        const handleForceShowModal = () => {
            if (window.deferredPrompt) {
                window.deferredPrompt.prompt();
            } else {
                // Determinar plataforma para mostrar las instrucciones correctas
                const userAgent = navigator.userAgent || navigator.vendor || window.opera;
                const isIOS = /iPad|iPhone|iPod/.test(userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
                const isAndroid = /Android/i.test(userAgent);

                if (isIOS) {
                    setActiveInstructions('ios');
                } else if (isAndroid) {
                    setActiveInstructions('android');
                } else {
                    setActiveInstructions('pc');
                }
                setShowPopup(true);
            }
        };

        // Capturar cuando la app se instala con éxito para guardarlo persistentemente
        const handleAppInstalled = () => {
            console.log('La aplicación PWA fue instalada con éxito');
            localStorage.setItem('pwa_installed', 'true');
            setIsStandalone(true);
        };

        window.addEventListener('pwa-prompt-available', handlePromptAvailable);
        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.addEventListener('show-pwa-install-modal', handleForceShowModal);
        window.addEventListener('appinstalled', handleAppInstalled);

        return () => {
            window.removeEventListener('pwa-prompt-available', handlePromptAvailable);
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('show-pwa-install-modal', handleForceShowModal);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);

    const handleInstallClick = () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            deferredPrompt.userChoice.then((choiceResult) => {
                if (choiceResult.outcome === 'accepted') {
                    console.log('Usuario aceptó la instalación');
                }
                setDeferredPrompt(null);
                setShowPopup(false);
            });
        } else {
            // Si no hay prompt automático (ej: iOS, navegadores sin soporte), mostrar las instrucciones detalladas
            const userAgent = navigator.userAgent || navigator.vendor || window.opera;
            const isIOS = /iPad|iPhone|iPod/.test(userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
            const isAndroid = /Android/i.test(userAgent);

            if (isIOS) {
                setActiveInstructions('ios');
            } else if (isAndroid) {
                setActiveInstructions('android');
            } else {
                setActiveInstructions('pc');
            }
        }
    };

    const handleClosePopup = () => {
        setShowPopup(false);
        setActiveInstructions(null);
    };

    // No mostrar absolutamente nada si PWA no está activo o si ya está instalada o no se debe mostrar el popup
    if (isStandalone || !pwaConfig || pwaConfig.activo !== 1 || !showPopup) {
        return null;
    }

    return (
        <div className={`installPwaPopup ${activeInstructions ? 'has-instructions' : ''}`}>
            <div className={`installPwaContent ${activeInstructions ? 'instructions-layout' : ''}`}>
                <button className='installPwaClose' onClick={handleClosePopup} aria-label="Cerrar">
                    <FontAwesomeIcon icon={faTimes} />
                </button>

                {!activeInstructions ? (
                    // --- VISTA INICIAL: INVITACIÓN DE INSTALACIÓN ---
                    <div className="pwa-intro-view">
                        {pwaConfig.logo && (
                            <img src={pwaConfig.logo} alt="Logo" className='installPwaLogo' />
                        )}
                        <h3>Instala nuestra App</h3>
                        <p>Descarga la aplicación en tu pantalla de inicio para navegar rápido y pedir sin límites.</p>
                        <div className='installPwaBtns'>
                            <button className='btnInstall' onClick={handleInstallClick}>
                                <FontAwesomeIcon icon={faDownload} /> Instalar App
                            </button>
                            <button className='btnClose' onClick={handleClosePopup}>
                                Quizás más tarde
                            </button>
                        </div>
                    </div>
                ) : (
                    // --- VISTA DETALLADA: PASOS FÁCILES PARA CUALQUIER DISPOSITIVO ---
                    <div className="pwa-instructions-view">
                        <h3>Guía de Instalación Rápida</h3>
                        <p className="subtitle">Configura el acceso directo en solo 3 segundos:</p>

                        {activeInstructions === 'ios' && (
                            <div className="instructions-list ios-steps">
                                <div className="step-card">
                                    <div className="step-number">1</div>
                                    <div className="step-text">
                                        Presiona el botón de <strong>Compartir</strong> <SafariShareIcon /> en la barra inferior de tu navegador Safari.
                                    </div>
                                </div>
                                <div className="step-card">
                                    <div className="step-number">2</div>
                                    <div className="step-text">
                                        Desplázate hacia abajo en el menú y selecciona <strong>"Agregar a inicio"</strong> <ApplePlusIcon />.
                                    </div>
                                </div>
                                <div className="step-card">
                                    <div className="step-number">3</div>
                                    <div className="step-text">
                                        Presiona <strong>"Agregar"</strong> en la esquina superior derecha. ¡Listo!
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeInstructions === 'android' && (
                            <div className="instructions-list android-steps">
                                <div className="step-card">
                                    <div className="step-number">1</div>
                                    <div className="step-text">
                                        Toca el botón de <strong>Menú</strong> <FontAwesomeIcon icon={faEllipsisV} style={{ margin: '0 4px', color: 'var(--primary-color, #03A3A8)' }} /> (tres puntos) en la esquina superior derecha del navegador.
                                    </div>
                                </div>
                                <div className="step-card">
                                    <div className="step-number">2</div>
                                    <div className="step-text">
                                        Selecciona <strong>"Instalar aplicación"</strong> o <strong>"Agregar a pantalla de inicio"</strong>.
                                    </div>
                                </div>
                                <div className="step-card">
                                    <div className="step-number">3</div>
                                    <div className="step-text">
                                        Confirma la instalación y disfruta de la aplicación de inmediato.
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeInstructions === 'pc' && (
                            <div className="instructions-list pc-steps">
                                <div className="step-card">
                                    <div className="step-number">1</div>
                                    <div className="step-text">
                                        Haz clic en el ícono de <strong>Instalar</strong> <FontAwesomeIcon icon={faLaptop} style={{ margin: '0 4px', color: 'var(--primary-color, #03A3A8)' }} /> (computadora con flecha) ubicado al lado derecho de la barra de direcciones superior.
                                    </div>
                                </div>
                                <div className="step-card">
                                    <div className="step-number">2</div>
                                    <div className="step-text">
                                        O bien, abre el menú del navegador <FontAwesomeIcon icon={faEllipsisV} style={{ margin: '0 4px' }} /> y haz clic en <strong>"Instalar"</strong>.
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className='installPwaBtns' style={{ marginTop: '20px' }}>
                            <button className='btnInstall btnDone' onClick={handleClosePopup}>
                                ¡Entendido!
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// Función helper exportable para compatibilidad con llamadas tradicionales de otros componentes
export const handleInstallPWA = (deferredPrompt) => {
    if (deferredPrompt) {
        deferredPrompt.prompt();
        deferredPrompt.userChoice.then((choiceResult) => {
            if (choiceResult.outcome === 'accepted') {
                console.log('Usuario aceptó la instalación');
            }
        });
    } else {
        // Enviar el evento global para lanzar la ventana con la guía correspondiente
        window.dispatchEvent(new CustomEvent('show-pwa-install-modal'));
    }
};
